import { useState, useCallback } from 'react';
import {
  Modal,
  Text,
  BlockStack,
  InlineStack,
  Button,
  TextField,
  Banner,
  ProgressBar,
  Icon,
  Divider,
} from '@shopify/polaris';
import {
  CheckCircleIcon,
  LinkIcon,
  ChartVerticalFilledIcon,
} from '@shopify/polaris-icons';
import { useShopifyFetch } from '../hooks/useShopifyFetch';

interface OnboardingWizardProps {
  open: boolean;
  onComplete: () => void;
  shopName?: string;
}

const STEPS = [
  {
    title: 'Welcome to Affilync',
    subtitle: 'The affiliate marketing platform for Shopify merchants',
  },
  {
    title: 'Connect Your Account',
    subtitle: 'Link your Affilync brand account to start tracking',
  },
  {
    title: 'You\'re All Set!',
    subtitle: 'Start growing your revenue with affiliate marketing',
  },
];

export function OnboardingWizard({
  open,
  onComplete,
  shopName,
}: OnboardingWizardProps) {
  const [step, setStep] = useState(0);
  const [brandId, setBrandId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState('');
  const [connected, setConnected] = useState(false);
  const { post } = useShopifyFetch();

  const progress = ((step + 1) / STEPS.length) * 100;

  const handleConnect = useCallback(async () => {
    if (!brandId.trim()) {
      setConnectError('Please enter your Brand ID');
      return;
    }
    if (!apiKey.trim()) {
      setConnectError('Please enter your Affilync brand API key');
      return;
    }
    setConnecting(true);
    setConnectError('');
    try {
      await post('/api/shop/connect', {
        brand_id: brandId.trim(),
        api_key: apiKey.trim(),
      });
      setConnected(true);
      setTimeout(() => setStep(2), 800);
    } catch (err) {
      // Surface the server's specific reason (brand not found vs wrong API key
      // vs inactive) instead of a generic message, so merchants can self-correct.
      const detail =
        (err as { body?: { detail?: string } })?.body?.detail ||
        (err as { message?: string })?.message;
      setConnectError(
        detail ||
          'Failed to connect. Check your Brand ID and API key, then try again.',
      );
    } finally {
      setConnecting(false);
    }
  }, [brandId, apiKey, post]);

  const handleFinish = useCallback(() => {
    onComplete();
  }, [onComplete]);

  return (
    <Modal
      open={open}
      onClose={onComplete}
      title={STEPS[step].title}
      primaryAction={
        step === 0
          ? { content: 'Get Started', onAction: () => setStep(1) }
          : step === 1
            ? {
                content: connected ? 'Continue' : 'Connect',
                onAction: connected ? () => setStep(2) : handleConnect,
                loading: connecting,
                disabled: (!brandId.trim() || !apiKey.trim()) && !connected,
              }
            : { content: 'Go to Dashboard', onAction: handleFinish }
      }
      secondaryActions={
        step > 0 && step < 2
          ? [
              {
                content: 'Skip for now',
                onAction: () => setStep(2),
              },
            ]
          : undefined
      }
    >
      <Modal.Section>
        <BlockStack gap="400">
          <ProgressBar progress={progress} size="small" tone="primary" />
          <Text variant="bodyMd" as="p" tone="subdued">
            Step {step + 1} of {STEPS.length} &mdash; {STEPS[step].subtitle}
          </Text>
          <Divider />

          {step === 0 && (
            <BlockStack gap="400">
              <Text variant="bodyLg" as="p">
                Hi{shopName ? `, ${shopName}` : ''}! Welcome to Affilync &mdash;
                the all-in-one affiliate marketing platform that helps Shopify
                merchants grow revenue through affiliate partnerships.
              </Text>
              <BlockStack gap="300">
                <InlineStack gap="300" blockAlign="center">
                  <Icon source={CheckCircleIcon} tone="success" />
                  <Text as="span" variant="bodyMd">
                    <strong>Track Conversions</strong> &mdash; Automatic order
                    tracking with affiliate attribution
                  </Text>
                </InlineStack>
                <InlineStack gap="300" blockAlign="center">
                  <Icon source={LinkIcon} tone="info" />
                  <Text as="span" variant="bodyMd">
                    <strong>Affiliate Links</strong> &mdash; Generate trackable
                    links for your products
                  </Text>
                </InlineStack>
                <InlineStack gap="300" blockAlign="center">
                  <Icon source={ChartVerticalFilledIcon} tone="info" />
                  <Text as="span" variant="bodyMd">
                    <strong>Real-time Analytics</strong> &mdash; See clicks,
                    conversions, and revenue in real time
                  </Text>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          )}

          {step === 1 && (
            <BlockStack gap="400">
              {connected ? (
                <Banner tone="success">
                  <p>
                    Successfully connected to Affilync! You can now import your
                    products from the Products page whenever you&apos;re ready.
                  </p>
                </Banner>
              ) : (
                <>
                  <Text variant="bodyMd" as="p">
                    Connect your store with your Affilync <strong>Brand ID</strong>{' '}
                    and a <strong>brand API key</strong>. Both are in your Affilync
                    dashboard: the Brand ID under{' '}
                    <strong>Settings &gt; Brand Profile</strong>, and an API key
                    under <strong>Settings &gt; API Keys</strong>. The API key
                    proves you own the brand.
                  </Text>
                  <TextField
                    label="Brand ID"
                    value={brandId}
                    onChange={setBrandId}
                    placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
                    autoComplete="off"
                    helpText="Your unique Affilync brand identifier (UUID format)"
                  />
                  <TextField
                    label="Brand API key"
                    type="password"
                    value={apiKey}
                    onChange={setApiKey}
                    placeholder="ak_live_…"
                    autoComplete="off"
                    helpText="Generate one in Affilync → Settings → API Keys. Required to verify ownership."
                  />
                  {connectError && (
                    <Banner tone="critical">
                      <p>{connectError}</p>
                    </Banner>
                  )}
                  <Text variant="bodySm" as="p" tone="subdued">
                    Don't have an account yet?{' '}
                    <Button variant="plain" url="https://app.affilync.com/register" external>
                      Sign up for free
                    </Button>
                  </Text>
                </>
              )}
            </BlockStack>
          )}

          {step === 2 && (
            <BlockStack gap="400">
              <Banner tone="success">
                <p>Your Shopify store is ready to use with Affilync!</p>
              </Banner>
              <Text variant="bodyMd" as="p">
                Here's what to do next:
              </Text>
              <BlockStack gap="200">
                <Text as="p" variant="bodyMd">
                  1. <strong>Import your products</strong> &mdash; go to{' '}
                  <strong>Products</strong> and select{' '}
                  <strong>Import Products</strong>, then{' '}
                  <strong>Sync to Marketplace</strong> to make them available
                  to affiliates
                </Text>
                <Text as="p" variant="bodyMd">
                  2. <strong>Orders are tracked</strong> and attributed to
                  affiliates in real time
                </Text>
                <Text as="p" variant="bodyMd">
                  3. <strong>Visit your dashboard</strong> to view analytics and
                  manage affiliate links
                </Text>
              </BlockStack>
              <Text as="p" variant="bodySm" tone="subdued">
                Tip: turn on <strong>Auto-sync new products</strong> in{' '}
                <strong>Settings</strong> to keep newly added products syncing
                to the marketplace automatically.
              </Text>
              <Divider />
              <InlineStack gap="300">
                <Button
                  variant="plain"
                  url="https://affilync.com/privacy"
                  external
                >
                  Privacy Policy
                </Button>
                <Button
                  variant="plain"
                  url="https://affilync.com/terms"
                  external
                >
                  Terms of Service
                </Button>
              </InlineStack>
            </BlockStack>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
